import { useState } from "react";
import Navigation from "../components/Navigation";
import Footer from "../components/Footer";
import FadeIn from "../components/FadeIn";
import SEO from "../components/SEO";

const ROLES = [
  {
    id: "product-lead",
    title: "Product Lead",
    summary: "Define the structure and evolution of the HAEVN system. Own how intent becomes matching.",
    detail: {
      description: "HAEVN is building a system where intent drives connection. This role defines how that system evolves over time. You'll work across product, matching logic, and user experience to ensure the platform stays focused on alignment over engagement.",
      focus: ["System design", "Matching experience", "Product clarity"],
    },
  },
  {
    id: "matching-systems-engineer",
    title: "Matching Systems Engineer",
    summary: "Design and refine the logic that powers 80%+ compatibility. Focus on signal, not noise.",
    detail: {
      description: "The matching engine is the core of HAEVN. This role owns the algorithms, data models, and scoring systems that determine compatibility. You'll work to continuously improve match quality while keeping the system honest and transparent.",
      focus: ["Compatibility algorithms", "Signal optimization", "Data modeling"],
    },
  },
  {
    id: "trust-safety-lead",
    title: "Trust & Safety Lead",
    summary: "Build systems that protect authenticity, privacy, and user integrity. Trust is the foundation of the network.",
    detail: {
      description: "HAEVN only works if people trust it. This role builds the verification, moderation, and privacy systems that keep the network safe. You'll define how authenticity is maintained without creating friction.",
      focus: ["Identity verification", "Privacy systems", "User integrity"],
    },
  },
  {
    id: "community-network-lead",
    title: "Community & Network Lead",
    summary: "Shape how HAEVN grows city by city. Curate the early network and define the culture.",
    detail: {
      description: "HAEVN launches one city at a time. This role owns how each city is built, activated, and nurtured. You'll shape the early community experience and ensure network quality from day one.",
      focus: ["City launches", "Network curation", "Community culture"],
    },
  },
  {
    id: "brand-creative-director",
    title: "Brand & Creative Director",
    summary: "Define how HAEVN shows up visually and culturally. Make the brand feel as intentional as the product.",
    detail: {
      description: "HAEVN's brand is a reflection of its product: calm, intentional, and premium. This role owns the visual identity, tone, and creative direction across every touchpoint.",
      focus: ["Visual identity", "Brand tone", "Creative direction"],
    },
  },
  {
    id: "partnerships-expansion-lead",
    title: "Partnerships & Expansion Lead",
    summary: "Drive how HAEVN enters and activates new markets. Focus on density, not reach.",
    detail: {
      description: "Growth at HAEVN is deliberate. This role identifies and develops partnerships that accelerate city-level adoption. You'll focus on building density in each market before moving to the next.",
      focus: ["Market entry", "Strategic partnerships", "Network density"],
    },
  },
  {
    id: "data-insights-lead",
    title: "Data & Insights Lead",
    summary: "Translate behavior into insight. Continuously improve matching quality and network health.",
    detail: {
      description: "Every interaction on HAEVN generates signal. This role turns that signal into actionable insight — improving match accuracy, identifying network health trends, and informing product decisions.",
      focus: ["Behavioral analytics", "Match quality metrics", "Network health"],
    },
  },
];

export default function CareersPage() {
  const [expandedRole, setExpandedRole] = useState(null);
  const [formData, setFormData] = useState({ name: "", email: "", linkedin: "", note: "" });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const toggleRole = (id) => setExpandedRole(expandedRole === id ? null : id);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/careers-interest`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...formData, source: "careers_page" }),
      });
      if (res.ok) setSubmitted(true);
    } catch (err) {
      console.error(err);
    }
    setSubmitting(false);
  };

  return (
    <div className="min-h-screen bg-white" data-testid="careers-page">
      <SEO
        title="Careers"
        path="/careers"
        description="Explore roles at HAEVN. We're building a matching network that connects singles based on compatibility, not algorithms designed to keep you swiping."
      />
      <Navigation />

      {/* Hero */}
      <section className="pt-28 pb-16 px-6" data-testid="careers-hero">
        <div className="max-w-[900px] mx-auto">
          <FadeIn>
            <h1 className="font-headline text-4xl sm:text-5xl lg:text-6xl text-haevn-navy leading-tight mb-4">
              Careers at HAEVN
            </h1>
            <p className="text-lg text-gray-600 font-body leading-relaxed mb-3 max-w-xl">
              We're building a better system for connection. These are the roles that make it possible.
            </p>
            <p className="text-sm text-gray-400 font-body">
              We're not actively hiring for all roles. We're building the network behind them.
            </p>
          </FadeIn>
        </div>
      </section>

      {/* Role List */}
      <section className="pb-20 px-6" data-testid="careers-roles">
        <div className="max-w-[900px] mx-auto flex flex-col gap-0">
          {ROLES.map((role, i) => {
            const isOpen = expandedRole === role.id;
            return (
              <FadeIn key={role.id} delay={i * 60}>
                <div className="border-t border-gray-200 py-8" data-testid={`role-${role.id}`}>
                  <h3 className="text-xl text-gray-900 font-body" style={{ fontWeight: 500 }}>
                    {role.title}
                  </h3>
                  <p className="text-base text-gray-500 font-body mt-2 leading-relaxed">
                    {role.summary}
                  </p>
                  <button
                    onClick={() => toggleRole(role.id)}
                    className="mt-3 text-sm text-gray-900 font-body underline cursor-pointer hover:text-haevn-teal transition-colors duration-200"
                    data-testid={`view-role-${role.id}`}
                  >
                    {isOpen ? "Close" : "View Role"}
                  </button>

                  {isOpen && (
                    <div
                      className="mt-6 pl-0 md:pl-6"
                      style={{ animation: "careersFadeIn 200ms ease forwards" }}
                    >
                      <p className="text-base text-gray-600 font-body leading-relaxed mb-5">
                        {role.detail.description}
                      </p>
                      <div className="mb-5">
                        <p className="text-xs font-subhead uppercase tracking-wider text-gray-400 mb-2">Focus Areas</p>
                        <div className="flex flex-wrap gap-3">
                          {role.detail.focus.map((f) => (
                            <span key={f} className="text-sm text-gray-600 font-body">
                              {f}
                            </span>
                          ))}
                        </div>
                      </div>
                      <p className="text-sm text-gray-400 font-body italic">
                        If this resonates, reach out.
                      </p>
                    </div>
                  )}
                </div>
              </FadeIn>
            );
          })}
          <div className="border-t border-gray-200" />
        </div>
      </section>

      {/* Interest Form */}
      <section className="py-20 md:py-28 px-6 bg-[#F3F2F0]" data-testid="careers-form-section">
        <div className="max-w-[600px] mx-auto">
          <FadeIn>
            <h2 className="font-headline text-2xl md:text-3xl text-gray-900 leading-tight mb-3">
              Interested in building this?
            </h2>
            <p className="text-base text-gray-500 font-body leading-relaxed mb-10">
              We're always open to connecting with people who think this way.
            </p>
          </FadeIn>

          {submitted ? (
            <FadeIn>
              <div className="bg-white border border-gray-200 rounded-xl p-8 text-center" data-testid="careers-form-success">
                <p className="text-lg text-gray-900 font-body mb-2" style={{ fontWeight: 500 }}>
                  Thanks for reaching out.
                </p>
                <p className="text-sm text-gray-500 font-body">
                  We'll be in touch if there's alignment.
                </p>
              </div>
            </FadeIn>
          ) : (
            <FadeIn delay={100}>
              <form onSubmit={handleSubmit} className="space-y-5" data-testid="careers-form">
                <div>
                  <label className="block text-sm text-gray-600 font-body mb-1">Name</label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-4 py-3 bg-white border border-gray-200 rounded-lg text-base text-gray-900 font-body focus:outline-none focus:border-gray-400 transition-colors"
                    data-testid="careers-name-input"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-600 font-body mb-1">Email</label>
                  <input
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full px-4 py-3 bg-white border border-gray-200 rounded-lg text-base text-gray-900 font-body focus:outline-none focus:border-gray-400 transition-colors"
                    data-testid="careers-email-input"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-600 font-body mb-1">LinkedIn or Website <span className="text-gray-400">(optional)</span></label>
                  <input
                    type="text"
                    value={formData.linkedin}
                    onChange={(e) => setFormData({ ...formData, linkedin: e.target.value })}
                    className="w-full px-4 py-3 bg-white border border-gray-200 rounded-lg text-base text-gray-900 font-body focus:outline-none focus:border-gray-400 transition-colors"
                    data-testid="careers-linkedin-input"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-600 font-body mb-1">Short note <span className="text-gray-400">(optional)</span></label>
                  <textarea
                    rows={3}
                    value={formData.note}
                    onChange={(e) => setFormData({ ...formData, note: e.target.value })}
                    className="w-full px-4 py-3 bg-white border border-gray-200 rounded-lg text-base text-gray-900 font-body focus:outline-none focus:border-gray-400 transition-colors resize-none"
                    data-testid="careers-note-input"
                  />
                </div>
                <button
                  type="submit"
                  disabled={submitting}
                  className="bg-haevn-navy text-white px-8 py-3 font-subhead text-base hover:bg-haevn-navy/90 transition-colors duration-200 disabled:opacity-50"
                  data-testid="careers-submit-btn"
                >
                  {submitting ? "Sending..." : "Start the conversation"}
                </button>
              </form>
            </FadeIn>
          )}
        </div>
      </section>

      <style>{`
        @keyframes careersFadeIn {
          from { opacity: 0; transform: translateY(6px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>

      <Footer />
    </div>
  );
}
